import { PlusCircleOutlined } from "@ant-design/icons"
import { formatCurrencyNumber } from "@finway-group/shared/lib/utils"
import { Button, Empty, Table, Tooltip } from "antd"
import { ColumnsType } from "antd/es/table"
import React from "react"
import { useTranslation } from "react-i18next"
import { generatePath, useHistory } from "react-router-dom"
import ExpenseStatusTag from "~Components/expenseStatusTag"
import { NewInvoiceIcon } from "~Components/icons/NewInvoiceIcon"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { formatOptionalDate } from "~Shared/utils/date.utils"
import { PurchaseOrderLinks } from "../types/PurchaseOrder.interfaces"

type PurchaseOrderInvoiceForLinking = PurchaseOrderLinks["invoices"][number]

const columns: ColumnsType<PurchaseOrderInvoiceForLinking> = [
    {
        key: "icon",
        width: 50,
        render: () => <NewInvoiceIcon className="text-blue-500" />,
    },
    {
        key: "expenseNumber",
        width: 150,

        render: (_, { expenseNumber, description }) => (
            <div className="flex flex-col">
                <Tooltip title={expenseNumber}>
                    <span className="font-medium max-w-120 truncate">#{expenseNumber}</span>
                </Tooltip>
                <Tooltip title={description}>
                    <span className="text-gray-500 max-w-120 truncate">{description}</span>
                </Tooltip>
            </div>
        ),
    },
    {
        key: "datePurchased",
        width: 100,
        render: (_, { datePurchased }) => formatOptionalDate(datePurchased),
    },
    {
        key: "amount",
        width: 80,
        render: (_, { totalGrossPrice, currency }) => <span className="font-semibold">{formatCurrencyNumber({ value: totalGrossPrice, currency })}</span>,
    },
    {
        key: "status",
        width: 100,
        render: (_, { status }) => <ExpenseStatusTag status={status} />,
    },
]

interface LinkedInvoicesSectionProps {
    purchaseOrderId?: string
    invoices?: Array<PurchaseOrderLinks["invoices"][number]>
    onAddInvoice?: () => void
    canAddInvoice?: boolean
}

export const LinkedInvoicesSection = ({ invoices = [], onAddInvoice, canAddInvoice = false }: LinkedInvoicesSectionProps) => {
    const { t } = useTranslation()
    const history = useHistory()

    return (
        <>
            <div className="mb-10 flex justify-between">
                {canAddInvoice && (
                    <Button type="primary" onClick={onAddInvoice} className="ml-auto">
                        <PlusCircleOutlined />
                        {t("purchaseOrders:details.addInvoice")}
                    </Button>
                )}
            </div>

            <Table
                columns={columns}
                dataSource={invoices ?? []}
                rowKey="_id"
                pagination={false}
                size="middle"
                showHeader={false}
                onRow={(record) => {
                    const hasReadAccess = record?.metadata?.userAccess?.read
                    return {
                        onClick: () => {
                            if (hasReadAccess) {
                                history.push(generatePath(RoutePathEnum.EXPENSE_DETAIL_PAGE, { id: record._id }))
                            }
                        },
                        style: {
                            cursor: hasReadAccess ? "default" : "not-allowed",
                        },
                    }
                }}
                className="w-full"
                locale={{
                    emptyText: (
                        <div className="animation-appear">
                            <Empty
                                className="animation-appear"
                                image={`./icons/empty_table.svg`}
                                imageStyle={{ height: 50, marginRight: "auto", marginLeft: "auto", display: "inline-block" }}
                                description={<span className="text-md font-medium mb-0">{t("info:no_linking_data_found.invoice.message")}</span>}
                            ></Empty>
                        </div>
                    ),
                }}
            />
        </>
    )
}
